/**
 * Utilidades para formateo de fechas
 * Soluciona problemas de zona horaria al parsear fechas del backend
 */

/**
 * Formatea una fecha string (YYYY-MM-DD) a formato local sin problemas de timezone
 * 
 * Problema: Cuando el backend envía "2025-11-03", JavaScript lo interpreta como
 * "2025-11-03T00:00:00Z" (medianoche UTC), y al convertir a zona horaria local
 * (ej: America/Mexico_City UTC-6), puede resultar en el día anterior.
 * 
 * Solución: Parseamos manualmente la fecha y usamos UTC para evitar conversiones
 * 
 * @param dateString - Fecha en formato ISO (YYYY-MM-DD) o ISO completo
 * @param format - Formato de salida ('short' | 'long' | 'numeric')
 * @returns Fecha formateada en español
 */
export const formatDateSafe = (
  dateString: string | null | undefined,
  format: 'short' | 'long' | 'numeric' = 'short'
): string => {
  if (!dateString) return 'No especificada'
  
  try {
    // Si es solo fecha (YYYY-MM-DD), parseamos manualmente para evitar timezone issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number)
      const date = new Date(year, month - 1, day) // Usar fecha local directamente
      
      return formatDateObject(date, format)
    }
    
    // Si tiene hora, usar Date normal
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return 'Fecha inválida'
    }
    
    return formatDateObject(date, format)
  } catch {
    return 'Fecha inválida'
  }
}

/**
 * Formatea un objeto Date según el formato especificado
 */
const formatDateObject = (date: Date, format: 'short' | 'long' | 'numeric'): string => {
  const formats: Record<typeof format, Intl.DateTimeFormatOptions> = {
    short: {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    },
    long: {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    },
    numeric: {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    }
  }
  
  return date.toLocaleDateString('es-MX', formats[format])
}

/**
 * Formatea una fecha para mostrar solo el día y mes
 */
export const formatDateShortMonthDay = (dateString: string | null | undefined): string => {
  if (!dateString) return 'No especificada'
  
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number)
      const date = new Date(year, month - 1, day)
      
      return date.toLocaleDateString('es-MX', {
        day: 'numeric',
        month: 'short'
      })
    }
    
    const date = new Date(dateString)
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short'
    })
  } catch {
    return 'Fecha inválida'
  }
}

/**
 * Convierte una fecha string a objeto Date de forma segura
 */
export const parseDateSafe = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null
  
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number)
      return new Date(year, month - 1, day)
    }
    
    const date = new Date(dateString)
    return isNaN(date.getTime()) ? null : date
  } catch {
    return null
  }
}

/**
 * La zona en la que se lee toda la operación (S-03).
 *
 * Un mismo evento salía con una hora distinta según dónde se viera: el sello y
 * los correos se formateaban en esta zona y el historial, el listado y el
 * dashboard en la del navegador. Para alguien en UTC−7 eso era una hora de
 * diferencia entre dos campos de la misma pantalla.
 *
 * El criterio es la hora de la operación, no la del dispositivo de quien mira,
 * y empata con el `BUSINESS_TIMEZONE` que el backend ya usa para los correos.
 * Al formatear una fecha de almacén, pasarla siempre.
 */
export const ZONA_DE_OPERACION = 'America/Mexico_City'

/**
 * `2026-10-01 11:32` en la zona de la operación (S-03).
 *
 * Varias pantallas de almacén armaban ese texto cortando la cadena ISO
 * (`iso.replace('T', ' ').slice(0, 16)`), que no convierte nada: mostraban UTC
 * crudo. El mismo rechazo salía a las 17:32 en el historial del responsable y a
 * las 11:32 en el sello.
 *
 * Mantiene el formato que ya tenían —el diseño lo pide así— y solo arregla la
 * zona. `sv-SE` se usa porque formatea en el orden ISO; no tiene nada que ver
 * con el idioma de la pantalla.
 */
/**
 * Normaliza lo que manda el servidor antes de convertirlo de zona.
 *
 * Varias columnas `timestamp` del back vuelven sin zona —`2026-10-01 17:32:00`—
 * y `new Date()` las interpreta en la zona del navegador, no en UTC. El
 * servidor corre en UTC, así que esa cadena es UTC y hay que decírselo; si no,
 * el instante se corre tantas horas como esté desfasado quien mira.
 *
 * Lo que ya trae `Z` o un desfase explícito se deja igual.
 */
export const comoInstante = (valor: string): Date => {
  const sinZona = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(valor)
  return new Date(sinZona ? `${valor.replace(' ', 'T')}Z` : valor)
}

/**
 * `2026-10-01` en la zona de la operación, para las fechas que se muestran sin
 * hora (S-03).
 *
 * Una fecha que ya viene sin hora no se toca: `YYYY-MM-DD` es un día del
 * calendario, y pasarlo por una zona lo correría al día anterior. Es la misma
 * trampa que cuida `formatDate`.
 */
export const formatFechaEnZona = (valor: string | null | undefined): string => {
  if (!valor) return 'Sin fecha'
  if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) return valor

  const fecha = comoInstante(valor)
  if (isNaN(fecha.getTime())) return valor

  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: ZONA_DE_OPERACION,
  }).format(fecha)
}

export const formatIsoEnZona = (iso: string | null | undefined): string => {
  if (!iso) return 'Sin fecha'
  const fecha = comoInstante(iso)
  if (isNaN(fecha.getTime())) return iso

  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: ZONA_DE_OPERACION,
  }).format(fecha)
}

/**
 * Formatea una fecha y hora con zona horaria de Ciudad de México
 * 
 * @param dateString - Fecha en formato ISO
 * @param opciones.segundos - Agrega los segundos a la hora (bitácoras de auditoría)
 * @returns Fecha y hora formateada en formato "dd/mm/yyyy, HH:MM"
 */
export const formatDateTimeMX = (
  dateString: string | null | undefined,
  opciones: { segundos?: boolean } = {},
): string => {
  if (!dateString) return 'No especificada'

  try {
    // Una cadena sin zona viene del servidor en UTC; sin decirlo, el navegador
    // la leería en su propia zona (SRH-329).
    const date = comoInstante(dateString)
    if (isNaN(date.getTime())) {
      return 'Fecha inválida'
    }

    // Formatear fecha
    const formattedDate = date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: ZONA_DE_OPERACION
    })

    // Formatear hora
    const formattedTime = date.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
      ...(opciones.segundos ? { second: '2-digit' } : {}),
      timeZone: ZONA_DE_OPERACION
    })

    return `${formattedDate}, ${formattedTime}`
  } catch {
    return 'Fecha inválida'
  }
}

/**
 * Formatea una fecha ISO a dd/mm/yyyy en locale es-MX.
 * Úsala cuando la fecha viene con hora del servidor (createdAt, updatedAt, etc.)
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-'

  // Una fecha sin hora (YYYY-MM-DD) la interpreta JavaScript como medianoche
  // UTC, y al pintarla en hora local (UTC-6) sale el dia anterior: una vigencia
  // capturada al 05/09 se mostraba como 04/09. Se arma con los componentes tal
  // cual vinieron, sin pasar por zona horaria.
  const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString)
  if (soloFecha) {
    const [, anio, mes, dia] = soloFecha
    return `${dia}/${mes}/${anio}`
  }

  // En la zona de la operación y no en la del navegador (SRH-329): una marca
  // de las 18:30 en Monterrey ya es el día siguiente en UTC, y según desde
  // dónde se mirara, o cómo llegara la cadena, salía con un día de más. Las
  // cadenas sin zona vienen del servidor en UTC.
  const date = comoInstante(dateString)
  if (isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: ZONA_DE_OPERACION,
  })
}
